'use client'

import { useCallback } from 'react'
import { useSession } from '@/contexts/SessionProvider'
import { useSnackbar } from '@/contexts/SnackBarContext'
import { useTexts } from '@/contexts/LanguageContext'
import { useNavigate } from '@/hooks'
import { useRouter } from 'next/navigation'
import { switchTenant } from '@/services/client/people/users'
import type { IOrganization } from '@/types/organization'

interface IUseOrganizationSwitchReturn {
  tenants: Pick<IOrganization, 'id' | 'name'>[]
  currentOrganization: IOrganization
  handleSwitchTenant: (tenantId: number) => Promise<void>
}

export function useOrganizationSwitch(onSwitch?: () => void): IUseOrganizationSwitchReturn {
  const session = useSession()
  const showSnackbar = useSnackbar()
  const { navigate } = useNavigate()
  const router = useRouter()
  const { TENANTS } = useTexts()

  const { user } = session
  const tenants = user.tenants || []
  const currentOrganization = user.simulatedOrganization || user.organization

  const handleSwitchTenant = useCallback(
    async (tenantId: number) => {
      onSwitch?.()
      const error = await switchTenant(tenantId)
      if (error) {
        showSnackbar(TENANTS.SNACKBAR.ERROR_SWITCHING, 'error')
      } else {
        showSnackbar(TENANTS.SNACKBAR.SWITCHED, 'success')
        navigate('/')
        router.refresh()
      }
    },
    [session, showSnackbar, TENANTS, navigate, router, onSwitch],
  )

  return {
    tenants,
    currentOrganization,
    handleSwitchTenant,
  }
}
